import React, {
  useEffect,
  useState,
  useRef,
  useContext,
  useCallback,
} from "react";
import {
  MapContainer,
  TileLayer,
  LayersControl,
  AttributionControl,
  ScaleControl,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import { AppSettings } from "../../config/app-settings.js";
import useModelMappingStore from "../../stores/model-mapping-store.js";
import { getStyle } from "./utils/map-styles.js";
import {
  createMarkerClusterGroup,
  safeAddToCluster,
  safeAddToMap,
} from "./utils/map-clustering.js";
import { getLayerPane, fitBoundsToLayers } from "./utils/map-geometry.js";
import {
  createTooltipContent,
  setupModelMappingPolygonInteractions,
  setupPointInteractions,
  setupLineInteractions,
} from "./utils/map-interactions.js";

// ==================== MAP CONFIGURATION ====================
const tileLayers = {
  Positron: {
    url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attribution/">CARTO</a>',
    maxZoom: 20,
  },
  OSM: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 20,
  },
  Google: {
    url: "https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}",
    attribution: "&copy; Google",
    maxZoom: 20,
    subdomains: ["mt0", "mt1", "mt2", "mt3"],
  },
  Hybrid: {
    url: "https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}",
    attribution: "&copy; Google",
    maxZoom: 20,
    subdomains: ["mt0", "mt1", "mt2", "mt3"],
  },
  Satellite: {
    url: "https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
    attribution: "&copy; Google",
    maxZoom: 20,
    subdomains: ["mt0", "mt1", "mt2", "mt3"],
  },
  Terrain: {
    url: "https://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}",
    attribution: "&copy; Google",
    maxZoom: 20,
    subdomains: ["mt0", "mt1", "mt2", "mt3"],
  },
  Humanitarian: {
    url: "https://tile-{s}.openstreetmap.fr/hot/{z}/{x}/{y}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 20,
  },
  "Dark Matter": {
    url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attribution/">CARTO</a>',
    maxZoom: 20,
  },
};

// ==================== LEAFLET ICON FIX ====================
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// ==================== MAP LAYER MANAGER ====================
const ModellingLayerManager = () => {
  const map = useMap();
  const context = useContext(AppSettings);

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const mapRef = useRef(null);
  const activeLayersRef = useRef(new Set());
  const layerGroupsRef = useRef(new Map());

  // Read from model-mapping store 
  const layerRegistry = useModelMappingStore((state) => state.layerRegistry);
  const visibleLayers = useModelMappingStore((state) => state.visibleLayers);

  const initializeMapPanes = () => {
    if (!map) return;

    const panes = [
      { name: "polygonPane", zIndex: 200 },
      { name: "polygonPane2", zIndex: 250 },
      { name: "linePane", zIndex: 300 },
      { name: "pointPane", zIndex: 400 },
    ];

    panes.forEach(({ name, zIndex }) => {
      if (!map.getPane(name)) {
        map.createPane(name);
        map.getPane(name).style.zIndex = zIndex;
      }
    });
  };

  const processPointFeatures = (pointFeatures, layerKey, newLayerGroups) => {
    if (pointFeatures.length === 0) return;

    const isSidebarOpen = context.appSidebarEndToggled;

    console.log(`📍 Processing points for ${layerKey}:`, {
      total: pointFeatures.length,
    });

    // For more than 1000 points, use clustering
    if (pointFeatures.length > 1000) {
      const markerClusterGroup = createMarkerClusterGroup(
        map,
        layerKey,
        pointFeatures.length
      );

      if (!markerClusterGroup) {
        console.warn(`Failed to create cluster group for ${layerKey}`);
        return;
      }

      let successfulMarkers = 0;

      pointFeatures.forEach((feature) => {
        try {
          const [lng, lat] = feature.geometry.coordinates;
          const pointStyle = getStyle("point", layerKey);

          const marker = L.circleMarker([lat, lng], {
            ...pointStyle,
            pane: "pointPane",
          });

          marker._getOriginalStyle = () => ({ ...pointStyle });
          setupPointInteractions(marker, feature, map, isSidebarOpen, false);

          const addedSuccessfully = safeAddToCluster(
            markerClusterGroup,
            marker,
            map,
            layerKey
          );
          if (addedSuccessfully) successfulMarkers++;
        } catch (error) {
          console.warn(`Error creating marker in ${layerKey}:`, error);
        }
      });

      if (successfulMarkers > 0) {
        const added = safeAddToMap(map, markerClusterGroup, layerKey);
        if (added) {
          newLayerGroups[`${layerKey}_clustered_points`] = markerClusterGroup;
          console.log(
            `✅ Added ${successfulMarkers} clustered points for ${layerKey}`
          );
        }
      }
    }

    // For 1000 points or fewer, add directly to map
    else {
      const pointGeoJSON = {
        type: "FeatureCollection",
        features: pointFeatures,
      };

      const pointLayer = L.geoJSON(pointGeoJSON, {
        pointToLayer: (feature, latlng) => {
          const pointStyle = getStyle("point", layerKey);
          const marker = L.circleMarker(latlng, {
            ...pointStyle,
            pane: "pointPane",
          });
          marker._getOriginalStyle = () => ({ ...pointStyle });
          return marker;
        },
        onEachFeature: (feature, layer) => {
          setupPointInteractions(layer, feature, map, isSidebarOpen, false);
        },
      });

      map.addLayer(pointLayer);
      newLayerGroups[`${layerKey}_individual_points`] = pointLayer;
      console.log(
        `✅ Added ${pointFeatures.length} individual points for ${layerKey}`
      );
    }
  };

  const processLineFeatures = (lineFeatures, layerKey, newLayerGroups) => {
    if (lineFeatures.length === 0) return;

    const isSidebarOpen = context.appSidebarEndToggled;

    const lineGeoJSON = {
      type: "FeatureCollection",
      features: lineFeatures,
    };

    const lineLayer = L.geoJSON(lineGeoJSON, {
      style: () => getStyle("line", layerKey),
      pane: "linePane",
      onEachFeature: (feature, layer) => {
        layer._getOriginalStyle = () => getStyle("line", layerKey);
        setupLineInteractions(layer, feature, map, isSidebarOpen);
      },
    });

    map.addLayer(lineLayer);
    newLayerGroups[`${layerKey}_lines`] = lineLayer;
    console.log(`✅ Added ${lineFeatures.length} lines for ${layerKey}`);
  };

  const processPolygonFeatures = (
    polygonFeatures,
    layerKey,
    newLayerGroups
  ) => {
    if (polygonFeatures.length === 0) return;

    const polygonGeoJSON = {
      type: "FeatureCollection",
      features: polygonFeatures,
    };

    const polygonLayer = L.geoJSON(polygonGeoJSON, {
      style: (feature) => getStyle("area", layerKey),
      pane: getLayerPane(layerKey, polygonFeatures[0]),
      onEachFeature: (feature, layer) => {
        const featureData = {
          ...feature.properties,
          _layerFilename: layerKey,
          _timestamp: Date.now(),
        };

        layer.feature.properties = featureData;
        layer._isSelected = false;
        layer._getOriginalStyle = () => getStyle("area", layerKey);

        // Use model-mapping specific polygon interactions
        setupModelMappingPolygonInteractions(
          layer,
          feature,
          map,
          layerKey,
          context
        );
      },
    });

    map.addLayer(polygonLayer);
    newLayerGroups[`${layerKey}_polygons`] = polygonLayer;
    console.log(`✅ Added ${polygonFeatures.length} polygons for ${layerKey}`);
  };

  const clearAllLayers = useCallback(() => {
    if (!map) return;

    console.log("🗑️ CLEARING ALL LAYERS (unmount)");

    layerGroupsRef.current.forEach((group) => {
      if (map.hasLayer(group)) {
        map.removeLayer(group);
      }
    });

    map.eachLayer((layer) => {
      if (layer instanceof L.GeoJSON || layer instanceof L.MarkerClusterGroup) {
        map.removeLayer(layer);
      }
    });

    activeLayersRef.current.clear();
    layerGroupsRef.current.clear();
  }, [map]);

  const syncAllLayers = useCallback(() => {
    if (!map || !layerRegistry) return;

    // Model-mapping only has static layers (no dynamic layers)
    const { static: allStaticLayers } = layerRegistry;

    // Filter by visibility
    const staticLayers = Object.keys(allStaticLayers)
      .filter((key) => visibleLayers[key] !== false)
      .reduce((acc, key) => {
        acc[key] = allStaticLayers[key];
        return acc;
      }, {});

    console.log("🔄 Syncing layers:", {
      staticKeys: Object.keys(staticLayers),
      visibleLayersState: visibleLayers,
    });

    const shouldBeVisible = new Set(Object.keys(staticLayers));
    const currentlyActive = activeLayersRef.current;

    const toAdd = [];
    const toRemove = [];

    shouldBeVisible.forEach((key) => {
      if (!currentlyActive.has(key)) toAdd.push(key);
    });

    currentlyActive.forEach((key) => {
      if (!shouldBeVisible.has(key)) toRemove.push(key);
    });

    console.log("📊 Layer diff:", { toAdd, toRemove });

    // Remove layers
    toRemove.forEach((key) => {
      console.log(`🗑️ Removing layer: ${key}`);

      const layerGroup = layerGroupsRef.current.get(key);
      if (layerGroup && map.hasLayer(layerGroup)) {
        map.removeLayer(layerGroup);
      }

      layerGroupsRef.current.delete(key);
      activeLayersRef.current.delete(key);
    });

    // Add layers
    if (toAdd.length > 0) {
      toAdd.forEach((key) => {
        console.log(`➕ Adding layer: ${key}`);

        const layerData = staticLayers[key];

        if (!layerData?.features?.length) {
          console.warn(`Cannot add layer ${key}: no features`);
          return;
        }

        try {
          // Separate by geometry type
          const pointFeatures = layerData.features.filter(
            (f) => f.geometry?.type === "Point"
          );
          const lineFeatures = layerData.features.filter(
            (f) =>
              f.geometry?.type === "LineString" ||
              f.geometry?.type === "MultiLineString"
          );
          const polygonFeatures = layerData.features.filter(
            (f) =>
              f.geometry?.type === "Polygon" ||
              f.geometry?.type === "MultiPolygon"
          );

          const tempLayerGroups = {};

          // Process each geometry type
          if (polygonFeatures.length > 0) {
            processPolygonFeatures(polygonFeatures, key, tempLayerGroups);
          }
          if (lineFeatures.length > 0) {
            processLineFeatures(lineFeatures, key, tempLayerGroups);
          }
          if (pointFeatures.length > 0) {
            processPointFeatures(pointFeatures, key, tempLayerGroups);
          }

          const allSubGroups = Object.values(tempLayerGroups);

          if (allSubGroups.length > 0) {
            let layerToStore;

            if (allSubGroups.length === 1) {
              layerToStore = allSubGroups[0];
              console.log(`✅ Stored single group for ${key}`);
            } else {
              layerToStore = L.featureGroup(allSubGroups);
              console.log(
                `✅ Combined ${allSubGroups.length} sub-groups for ${key}`
              );
            }

            layerGroupsRef.current.set(key, layerToStore);
            activeLayersRef.current.add(key);
          }
        } catch (error) {
          console.error(`Error processing layer ${key}:`, error);
        }
      });

      // Initial zoom to all layers
      if (isInitialLoad && layerGroupsRef.current.size > 0) {
        const isSidebarOpen = context.appSidebarEndToggled;
        console.log("layerGroupsRef.current:", layerGroupsRef.current);
        fitBoundsToLayers(map, layerGroupsRef.current, isSidebarOpen, () => {
          setIsInitialLoad(false);
        });
        console.log("✅ Initial load complete");
      }
    }
  }, [
    map,
    layerRegistry,
    visibleLayers,
    context.appSidebarEndToggled,
    isInitialLoad,
  ]);

  // ==================== EFFECTS ====================
  // Handle map ref, panes initialization and cleanup
  useEffect(() => {
    if (!map) return;

    mapRef.current = map;
    
    useModelMappingStore.getState().setMapReferences(map, layerGroupsRef);

    initializeMapPanes();

    const timer = setTimeout(() => {
      map.invalidateSize();
      console.log("🗺️ Map size invalidated");
    }, 200);

    return () => {
      if (map) {
        console.log("🧹 Map unmounting - clearing all layers and state");

        clearAllLayers();
        useModelMappingStore.getState().clearMapReferences();
        clearTimeout(timer);

        console.log("Map layers cleanup complete");
      }
    };
  }, [map, clearAllLayers]);

  // Sync layers when registry or visibility changes
  useEffect(() => {
    syncAllLayers();
  }, [syncAllLayers]);

  return null;
};

// ==================== MAIN MAP COMPONENT ====================
const ModellingMap = ({ center, zoom, height = "100%", width = "100%" }) => {
  const defaultCenter = center || [48.4284, -123.3656]; // Victoria, BC
  const defaultZoom = zoom || 10;

  return (
    <div style={{ height, width }}>
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        zoomDelta={0.25}
        zoomSnap={0}
        style={{ height: "100%", width: "100%" }}
      >
        <LayersControl position="topleft">
          {Object.entries(tileLayers).map(([name, config]) => (
            <LayersControl.BaseLayer
              key={name}
              checked={name === "OSM"} 
              name={name}
            >
              <TileLayer {...config} />
            </LayersControl.BaseLayer>
          ))}
        </LayersControl>

        <AttributionControl
          prefix='Vector data &copy; <a href="https://www.acuere.ca">Acuere Consulting Inc.</a>'
          position="bottomleft"
        />

        <ScaleControl maxWidth={150} position="bottomleft" />

        <ModellingLayerManager />
      </MapContainer>
    </div>
  );
};

export default ModellingMap;
